export const normalizeNormalDistribution = (responseData) => {
    const { histogramData, fittedNormalCurve, omega, ...rest } = responseData;

    return {
        ...rest,
        normalizedHistogramData: (histogramData || []).map((bin) => ({
            x: bin.x,
            binStart: bin.binStart,
            binEnd: bin.binEnd,
            y: bin.y,
        })),
        fittedNormalCurve,
        omega,
    };
};

export const buildNormalDistributionChartData = (normalDistData) => {
    if (!normalDistData?.normalizedHistogramData || !normalDistData?.fittedNormalCurve) {
        return { labels: [], datasets: [] };
    }

    return {
        datasets: [
            {
                type: 'bar',
                label: 'Histogram',
                data: normalDistData.normalizedHistogramData.map((bin) => ({
                    x: bin.x,
                    y: bin.y,
                    binStart: bin.binStart,
                    binEnd: bin.binEnd,
                })),
                backgroundColor: 'rgba(88, 112, 232, 0.72)',
                borderColor: 'rgba(88, 112, 232, 0.9)',
                borderWidth: 1,
                borderRadius: 3,
                barPercentage: 0.92,
                categoryPercentage: 0.92,
                barThickness: 'flex',
                order: 2,
            },
            {
                type: 'line',
                label: 'Normal distribution curve',
                data: normalDistData.fittedNormalCurve.map((point) => ({
                    x: point.x,
                    y: point.y,
                })),
                borderColor: '#e1008a',
                backgroundColor: '#e1008a',
                borderWidth: 3,
                fill: false,
                pointRadius: 0,
                pointHoverRadius: 4,
                tension: 0.35,
                order: 1,
            },
        ],
    };
};

export const buildNormalDistributionOptions = (normalDistData) => {
    if (!normalDistData) return {};

    const { lsl, usl, xBar, minVal, maxVal, axisMin, axisMax } = normalDistData;
    const annotations = {};
    const maxCount = Math.max(
        0,
        ...(normalDistData.normalizedHistogramData || []).map((bin) => Number(bin.y) || 0),
        ...(normalDistData.fittedNormalCurve || []).map((point) => Number(point.y) || 0)
    );

    if (Number.isFinite(Number(lsl))) {
        annotations.lsl = {
            type: 'line',
            xMin: lsl,
            xMax: lsl,
            borderColor: '#d64545',
            borderWidth: 2,
            label: {
                content: `LSL (${Number(lsl).toFixed(2)})`,
                enabled: true,
                position: 'top',
                backgroundColor: 'rgba(214, 69, 69, 0.85)',
                color: '#fff',
            },
        };
    }

    if (Number.isFinite(Number(usl))) {
        annotations.usl = {
            type: 'line',
            xMin: usl,
            xMax: usl,
            borderColor: '#5b8def',
            borderWidth: 2,
            label: {
                content: `USL (${Number(usl).toFixed(2)})`,
                enabled: true,
                position: 'top',
                backgroundColor: 'rgba(91, 141, 239, 0.85)',
                color: '#fff',
            },
        };
    }

    if (Number.isFinite(Number(xBar))) {
        annotations.mean = {
            type: 'line',
            xMin: xBar,
            xMax: xBar,
            borderColor: '#2f9e44',
            borderWidth: 2,
            label: {
                content: `Mean (${Number(xBar).toFixed(2)})`,
                enabled: true,
                position: 'top',
                backgroundColor: 'rgba(47, 158, 68, 0.85)',
                color: '#fff',
            },
        };
    }

    return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        scales: {
            x: {
                type: 'linear',
                min: Number.isFinite(Number(axisMin ?? minVal)) ? Number(axisMin ?? minVal) : undefined,
                max: Number.isFinite(Number(axisMax ?? maxVal)) ? Number(axisMax ?? maxVal) : undefined,
                title: { display: true, text: 'Measure Value' },
                grid: { color: 'rgba(148, 163, 184, 0.22)' },
                ticks: {
                    callback: (value) => Number(value).toFixed(2),
                },
            },
            y: {
                beginAtZero: true,
                suggestedMax: maxCount ? Math.ceil(maxCount * 1.15) : undefined,
                title: { display: true, text: 'Occurrences' },
                grid: { color: 'rgba(148, 163, 184, 0.22)' },
                ticks: {
                    precision: 0,
                },
            },
        },
        plugins: {
            title: {
                display: true,
                text: 'Normal Distribution Curve',
                font: { size: 14, weight: '700' },
                padding: { bottom: 12 },
            },
            legend: {
                position: 'top',
                labels: {
                    usePointStyle: true,
                    boxWidth: 14,
                    boxHeight: 8,
                },
            },
            tooltip: {
                callbacks: {
                    label: (tooltipItem) => {
                        const raw = tooltipItem.raw || {};
                        if (Number.isFinite(raw.binStart) && Number.isFinite(raw.binEnd)) {
                            return `${tooltipItem.dataset.label}: ${raw.y} in [${raw.binStart.toFixed(3)}, ${raw.binEnd.toFixed(3)}]`;
                        }
                        const value = Number(tooltipItem.parsed?.y);
                        return `${tooltipItem.dataset.label}: ${Number.isFinite(value) ? value.toFixed(3) : tooltipItem.raw}`;
                    },
                },
            },
            annotation: { annotations },
        },
    };
};

export const getCapabilityMetrics = (normalDistData) => [
    { label: 'Cp', value: Number.isFinite(normalDistData?.cp) ? normalDistData.cp.toFixed(2) : 'N/A', tone: 'neutral' },
    { label: 'Cpk', value: Number.isFinite(normalDistData?.cpk) ? normalDistData.cpk.toFixed(2) : 'N/A', tone: 'neutral' },
    { label: 'Records', value: normalDistData?.recordCount || normalDistData?.values?.length || 0, tone: 'neutral' },
    { label: 'Bin width', value: normalDistData?.omega ? normalDistData.omega.toFixed(2) : 'N/A', tone: 'neutral' },
    { label: 'Mean', value: Number.isFinite(Number(normalDistData?.xBar)) ? Number(normalDistData.xBar).toFixed(2) : 'N/A', tone: 'pass' },
];
